import { useDataLayerStore } from '~/store/dataLayer';

function pushEvent (eventName: string, event: string, eventInfo: any = null): void {
  if (import.meta.client) {
    window.nhstDataLayer = window.nhstDataLayer || {};
    window.nhstDataLayer.events = window.nhstDataLayer.events || [];

    const currentDateTime = new Date().getTime();
    const call = {
      event,
      eventName,
      timestamp: currentDateTime,
      environmentInfo: { ...useDataLayerStore().environmentInfo },
      pageInfo: { ...useDataLayerStore().pageInfo }
    };
    if (eventInfo) {
      call.eventInfo = eventInfo;
    }
    window.nhstDataLayer.events.push(call);
  }
}

function constructEventInfo (el: HTMLElement | null = null, data = {}, vnode: VNode | null = null) {
  const eventInfo = { action: {}, content: {} };
  if (typeof data !== 'string') {
    for (const field in data) {
      if (['component', 'element', 'elementId', 'elementName', 'elementPosition', 'linkUrl', 'teaserType', 'rowIndex', 'rowLayout', 'streamId', 'elementText'].includes(field)) {
        eventInfo.action[field] = data[field];
      } else if (!['eventName', 'action', 'trackOptions'].includes(field)) {
        eventInfo.content[field] = data[field];
      }
    }
  }
  const driveIntoChild = (element, origin) => {
    if (element === origin) {
      return true;
    } else if (element.children && element.children.length > 0) {
      for (let i = 0; i < element.children.length; i++) {
        const result = driveIntoChild(element.children[i], origin);
        if (result) {
          return true;
        }
      }
    }
    return false;
  };

  const findParentWithDataAttr = (attr, element) => {
    let parent = element.parentElement;

    while (parent) {
      if (parent.hasAttribute(`data-${attr}`)) {
        return parent;
      } else if (attr === 'list' && parent.hasAttribute('data-zone')) {
        return null;
      }
      parent = parent.parentElement;
    }

    return null;
  };

  if (!eventInfo.action.linkUrl && (vnode?.props?.href || el?.getAttribute('href'))) {
    eventInfo.action.linkUrl = vnode?.props?.href || el?.getAttribute('href');
  }

  if (!eventInfo.action.elementId) {
    if (vnode?.props?.id || el?.getAttribute('id')) {
      eventInfo.action.elementId = vnode?.props?.id || el?.getAttribute('id');
    } else {
      const nearestDataList = findParentWithDataAttr('list', el);

      if (nearestDataList?.id) {
        eventInfo.action.elementId = nearestDataList?.id;
      } else {
        const nearestDataZone = findParentWithDataAttr('zone', el);

        if (nearestDataZone?.id) {
          eventInfo.action.elementId = nearestDataZone?.id;
        }
      }
    }
  }

  if (!eventInfo.action.teaserType && el?.getAttribute('data-teaser_type')) {
    eventInfo.action.teaserType = el.getAttribute('data-teaser_type');
  }

  if (!eventInfo.action.elementName && (vnode?.props?.class || el?.getAttribute('class'))) {
    eventInfo.action.elementName = (vnode?.props?.class || el?.getAttribute('class')).split(' ').find(i => i.startsWith('dn-')) || eventInfo.action.elementId || '';
  }

  if (!eventInfo.action.component) {
    const nearestDataZone = findParentWithDataAttr('zone', el);
    if (nearestDataZone !== null) {
      if (nearestDataZone.getAttribute('data-zone')) {
        eventInfo.action.component = nearestDataZone.getAttribute('data-zone');
      } else if (nearestDataZone.getAttribute('id')) {
        eventInfo.action.component = nearestDataZone.getAttribute('id');
      } else if (nearestDataZone.getAttribute('class')) {
        const key = nearestDataZone.getAttribute('class').split(' ').find(i => i.startsWith('dn-'));
        if (key) {
          eventInfo.action.component = key.replace('dn-', '');
        }
      }
    }
  }

  if (!eventInfo.action.elementPosition) {
    if (el) {
      const clickedRow = el.closest('.dn-grid-layout');
      if (clickedRow) {
        const rows = Array.from(document.querySelectorAll('.dn-grid-layout'));
        const index = rows.indexOf(clickedRow);
        if (index !== -1) {
          eventInfo.action.rowIndex = index + 1;

          const layoutClass = [...clickedRow.classList].find(cls => cls.startsWith('layout-'));
          const layout = layoutClass?.split('-')[1];

          if (layout) {
            eventInfo.action.rowLayout = layout;
          }
        }
      }

      const nearestDataList = findParentWithDataAttr('list', el);

      if (nearestDataList?.children && nearestDataList.children.length > 0) {
        const dataListOrder = nearestDataList.getAttribute('data-list');
        for (let i = 0; i < nearestDataList.children.length; i++) {
          if (driveIntoChild(nearestDataList.children[i], el)) {
            eventInfo.action.elementPosition = `${dataListOrder && dataListOrder === 'reverse' ? nearestDataList.children.length - i : (i + 1)}/${nearestDataList.children.length}`;
            break;
          }
        }
      }
    }
  }

  if (!eventInfo.action.streamId) {
    const nearestDataStreamId = findParentWithDataAttr('streamId', el);
    if (nearestDataStreamId !== null) {
      eventInfo.action.streamId = nearestDataStreamId.getAttribute('data-streamId');
    }
  }
  return eventInfo;
}

export const useDataLayerEvent = () => {
  const pageView = () => {
    pushEvent('Page loaded', 'PAGE_VIEW');
  };
  const userEventById = (eventName: string, name: string, data: {}) => {
    const el = document.getElementById(name);
    if (el) {
      pushEvent(eventName, 'USER_ACTION', constructEventInfo(el, data));
    }
  };

  const handleDataLayerInteractionEvent = (interaction: any, target: HTMLElement | null) => {
    const action = interaction?.action;

    if (action === 'logo-click') {
      const eventData = {
        eventName: 'Click | Header',
        component: 'header',
        elementName: 'dn-logo',
        action: 'click',
        linkUrl: 'dn.no'
      };
      const eventInfo = constructEventInfo(target, eventData);
      eventInfo.action.action = 'click';
      userEvent('Click | Header', eventInfo);
    } else if (action === 'hot-topic-nav-click') {
      const parentElement = target?.parentElement?.parentElement;
      let elementPosition = '-1/0';
      if (target && parentElement) {
        const clickedListItem = target.closest('li');
        if (clickedListItem) {
          const allListItems = parentElement.querySelectorAll('li');
          const clickedIndex = Array.from(allListItems).indexOf(clickedListItem as HTMLLIElement);
          const totalItems = allListItems.length;
          if (clickedIndex !== -1) {
            elementPosition = `${clickedIndex + 1}/${totalItems}`;
          }
        }
      }
      const eventData = {
        eventName: 'Click | Header',
        component: 'header',
        elementName: 'dn-navigation-menu',
        linkUrl: 'dn.no',
        elementPosition
      };
      const eventInfo = constructEventInfo(target, eventData);
      eventInfo.action.action = 'click';
      userEvent(eventData.eventName, eventInfo);
    } else if (action === 'menu-toggle') {
      const eventData = {
        eventName: 'Click | Header',
        component: 'header',
        elementName: 'dn-burger-menu',
        elementId: interaction?.data?.isOpen ? 'menu-open' : 'menu-close'
      };
      const eventInfo = constructEventInfo(target, eventData);
      eventInfo.action.action = 'click';
      userEvent(eventData.eventName, eventInfo);
    } else if (action === 'menu-item-click') {
      const eventData = {
        eventName: 'Click | Header',
        action: 'click',
        component: 'header',
        elementName: 'dn-burger-menu',
        elementId: 'menu-navigation',
        linkUrl: interaction?.data?.to
      };
      const eventInfo = constructEventInfo(target, eventData);
      eventInfo.action.action = 'click';
      userEvent(eventData.eventName, eventInfo);
    }
  };

  const userEvent = (eventName: string, eventInfo = {}) => {
    pushEvent(eventName, 'USER_ACTION', eventInfo);
  };
  const systemEventById = (eventName: string, name: string, data: {}) => {
    const el = document.getElementById(name);
    if (el) {
      pushEvent(eventName, 'SYSTEM_ACTION', constructEventInfo(el, data));
    }
  };
  const systemEvent = (eventName: string, eventInfo = {}) => {
    pushEvent(eventName, 'SYSTEM_ACTION', eventInfo);
  };
  const performanceEvent = (eventName: string, eventInfo = {}) => {
    pushEvent(eventName, 'PERFORMANCE_REPORT', eventInfo);
  };

  return {
    handleDataLayerInteractionEvent,
    constructEventInfo,
    pageView,
    userEvent,
    userEventById,
    systemEvent,
    systemEventById,
    performanceEvent
  };
};
